import React, { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Download, FilePlus, Sparkles, Wand2, X } from 'lucide-react';

/**
 * CpscUploadPreflight — CHA-544 (C). Shows what `preflightManualCoc` found in an uploaded Manual CoC workbook,
 * ALL at once, before any draft is created. The operator ticks the fixes they accept and downloads a corrected
 * copy of their own file (to re-upload, and to send back to the lab). Nothing is applied silently:
 *   auto     deterministic fixes, pre-ticked (untick to keep the original)
 *   propose  judgement calls, unticked; a certificate type needs an explicit GCC/CPC choice
 *   flag     no fix is offered — the operator or the lab has to decide
 * "Create drafts" still sends the file AS UPLOADED (Ron, 2026-10-18): clean reports go through and the rest are
 * rejected with the same reasons shown here.
 *
 * CHA-544 (D) — "Ask AI to fix" sends the flagged cells to `fixManualCocWithAI`. Its proposals join the list as
 *   ai       unticked; RED ones (cited standards, an identifier, the lab ID) carry their own warning
 * Every AI proposal already passed the server's re-check; it is applied only to the downloaded copy, and only
 * if ticked. One fix per cell: ticking one unticks any other fix for the same cell.
 */
export const KIND = { // CHA-666 — exported so the BV upload's product-ID review shares the same badges
  auto: { label: 'Auto-fix', cls: 'bg-carbon-green-10 text-carbon-ink border-carbon-green-50' },
  propose: { label: 'Needs a decision', cls: 'bg-carbon-white text-carbon-orange-70 border-carbon-orange-40' },
  flag: { label: 'Fix by hand', cls: 'bg-carbon-white text-carbon-red-60 border-carbon-red-40' },
  ai: { label: 'AI suggestion', cls: 'bg-carbon-blue-10 text-carbon-blue-60 border-carbon-blue-60' },
  aiRed: { label: 'AI · check carefully', cls: 'bg-carbon-white text-carbon-red-60 border-carbon-red-60' },
};
const AI_RULES = new Set(['manufacture_month', 'date_format', 'citation_unregistered', 'style_glued', 'pid_hash', 'lab_id']);
const kindKey = (f) => (f.kind === 'ai' && f.red ? 'aiRed' : f.kind);
const cellKey = (f) => `${f.row}\u0000${f.header}`;
const RED_NOTE = 'This changes which standards are cited, an identifier, or the lab ID. Tick it only if the lab\'s report says so.';

// The AI's proposals as findings: same shape, kind 'ai', never pre-ticked.
export function aiFindings(ai) {
  return (ai?.proposals || []).map((p) => ({ ...p, id: `ai-${p.id}`, kind: 'ai', reason: p.reason || 'Reformatted by the AI.' }));
}
const fixable = (f) => f.kind !== 'flag' && f.header && f.row != null;

export function initialTicks(findings) { // AI proposals arrive later and so start unticked
  return Object.fromEntries((findings || []).filter(fixable).map((f) => [f.id, f.kind === 'auto']));
}

// The fixes to write: ticked, with a value (a certificate type needs its GCC/CPC choice first).
export function chosenFixes(findings, ticks, choices) {
  return (findings || []).filter((f) => fixable(f) && ticks[f.id]).map((f) => ({
    row: f.row, header: f.header, willBe: f.choices ? (choices[f.id] || null) : f.willBe,
  })).filter((x) => x.willBe != null && x.willBe !== '');
}

export default function CpscUploadPreflight({ result, fileName, busy, onDownload, onCreate, onCancel, ai, aiBusy, onAskAi }) {
  const baseFindings = useMemo(() => result?.findings || [], [result]);
  const findings = useMemo(() => [...baseFindings, ...aiFindings(ai)], [baseFindings, ai]);
  // Shown while a flagged cell the AI may try exists (the server's AI_RULES, functions/cpscManualCocAiFix.js), and
  // again after a failed attempt that produced nothing, so a rate limit or timeout can be retried.
  const canAskAi = !!onAskAi && (!ai || (ai.aiError && !(ai.proposals || []).length))
    && baseFindings.some((f) => f.kind === 'flag' && AI_RULES.has(f.rule) && f.header && f.row != null && f.was);
  const reports = useMemo(() => result?.reports || [], [result]);
  const summary = result?.summary || {};
  const [ticks, setTicks] = useState(() => initialTicks(baseFindings));
  // One fix per cell: ticking a fix unticks every other fix for the same cell.
  const setTick = (f, on) => setTicks((t) => {
    const next = { ...t };
    if (on) for (const o of findings) if (o.id !== f.id && fixable(o) && cellKey(o) === cellKey(f)) next[o.id] = false;
    next[f.id] = on;
    return next;
  });
  const [choices, setChoices] = useState({});
  const fixes = useMemo(() => chosenFixes(findings, ticks, choices), [findings, ticks, choices]);
  const reasonOf = useMemo(() => Object.fromEntries(reports.map((r) => [r.reportNo, r])), [reports]);
  const byReport = useMemo(() => {
    const m = new Map();
    for (const f of findings) { if (!m.has(f.reportNo)) m.set(f.reportNo, []); m.get(f.reportNo).push(f); }
    // A report can be rejected with no cell finding (the CHA-268 children's-GCC backstop) — it must still be listed.
    for (const r of reports) if (r.outcome === 'reject' && !m.has(r.reportNo)) m.set(r.reportNo, []);
    return [...m.entries()];
  }, [findings, reports]);

  return (
    <div className="bg-carbon-white border border-carbon-border p-3 space-y-3" data-testid="upload-preflight">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-carbon-ink flex items-center gap-1.5"><Wand2 size={14} /> Pre-flight{fileName ? `: ${fileName}` : ''}</h4>
          <p className="text-xs text-carbon-gray-70 mt-0.5" data-testid="preflight-summary">
            {summary.reports || 0} report(s): <strong>{summary.wouldCreate || 0}</strong> would be created, <strong>{summary.wouldReject || 0}</strong> would be rejected
            {summary.skippedRows ? <> · <strong>{summary.skippedRows}</strong> row(s) with no Report No would be skipped</> : null}
            {summary.alreadyCertified ? <> · <strong>{summary.alreadyCertified}</strong> already certified — did you mean to send only the new ones?</> : null}
          </p>
        </div>
        <button type="button" onClick={onCancel} disabled={busy} aria-label="Close pre-flight" className="text-carbon-gray-60 hover:text-carbon-ink disabled:opacity-40"><X size={16} /></button>
      </div>

      {!byReport.length ? (
        <div className="text-sm text-carbon-gray-80 bg-carbon-green-10 border border-carbon-green-50 px-3 py-2 flex items-center gap-1" data-testid="preflight-clean">
          <CheckCircle2 size={14} className="text-carbon-green-50" /> No problems found. Every report is ready to create.
        </div>
      ) : (
        <div className="space-y-2 max-h-[28rem] overflow-y-auto">
          {byReport.map(([reportNo, list]) => (
            <div key={reportNo} className="border border-carbon-border" data-testid={`preflight-report-${reportNo}`}>
              <div className="px-2 py-1 bg-carbon-gray-10 text-xs flex flex-wrap items-center gap-2">
                <span className="font-plex-mono font-semibold">{reportNo || '(no Report No)'}</span>
                {reasonOf[reportNo] && (reasonOf[reportNo].outcome === 'reject'
                  ? <span className="text-carbon-red-60" data-testid={`preflight-reason-${reportNo}`}>would be rejected as uploaded{reasonOf[reportNo].reason ? `: ${reasonOf[reportNo].reason}` : ''}</span>
                  : <span className="text-carbon-green-50">would be created</span>)}
              </div>
              {list.length > 0 && <table className="w-full text-xs">
                <tbody>
                  {list.map((f) => (
                    <tr key={f.id} className="border-t border-carbon-border align-top" data-testid={`finding-${f.id}`}>
                      <td className="px-2 py-1 w-6">
                        {fixable(f) ? (
                          <input type="checkbox" aria-label={`Apply ${f.kind === 'ai' ? 'AI ' : ''}fix: ${f.header} row ${f.row}`} checked={!!ticks[f.id]}
                            onChange={(e) => setTick(f, e.target.checked)} />
                        ) : <AlertTriangle size={12} className="text-carbon-red-60 mt-0.5" aria-label="No automatic fix" />}
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap text-carbon-gray-70">{f.row != null ? `Row ${f.row}` : ''}{f.header ? ` · ${f.header}` : ''}</td>
                      <td className="px-2 py-1">
                        <span className={`inline-block border px-1 text-[10px] uppercase tracking-[0.04em] ${KIND[kindKey(f)]?.cls || ''}`}>{KIND[kindKey(f)]?.label || f.kind}</span>
                      </td>
                      <td className="px-2 py-1 w-full">
                        {f.was !== undefined && f.was !== '' && (
                          <div className="font-plex-mono">
                            <span className="line-through text-carbon-gray-60">{f.was}</span>
                            {f.choices ? (
                              <> → <select aria-label={`Choose ${f.header} row ${f.row}`} value={choices[f.id] || ''} className="border border-carbon-border text-xs"
                                onChange={(e) => { const v = e.target.value; setChoices((c) => ({ ...c, [f.id]: v })); if (v) setTick(f, true); }}>
                                <option value="">choose…</option>
                                {f.choices.map((c) => <option key={c} value={c}>{c}</option>)}
                              </select></>
                            ) : f.willBe != null ? <> → <span className="text-carbon-ink">{f.willBe}</span></> : null}
                          </div>
                        )}
                        <div className="text-carbon-gray-70">{f.reason}</div>
                        {f.kind === 'ai' && f.red && <div className="text-carbon-red-60" data-testid={`ai-red-${f.id}`}>{RED_NOTE}</div>}
                        {f.kind === 'ai' && <div className="text-carbon-gray-60">{f.model}{f.escalated ? ' (escalated)' : ''}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>}
            </div>
          ))}
        </div>
      )}

      {ai && <AiSummary ai={ai} />}

      <div className="flex flex-wrap items-center gap-2">
        {canAskAi && (
          <button type="button" onClick={onAskAi} disabled={busy || aiBusy} data-testid="ask-ai"
            className="flex items-center gap-1 px-2 py-1 text-xs border border-carbon-purple-60 text-carbon-purple-60 hover:bg-carbon-gray-10 disabled:opacity-40">
            <Sparkles size={13} /> {aiBusy ? 'Asking the AI…' : 'Ask AI to fix the flagged cells'}
          </button>
        )}
        <button type="button" onClick={() => onDownload(fixes)} disabled={busy || !fixes.length}
          className="flex items-center gap-1 px-2 py-1 text-xs border border-carbon-blue-60 text-carbon-blue-60 hover:bg-carbon-blue-10 disabled:opacity-40">
          <Download size={13} /> Download corrected copy ({fixes.length} change{fixes.length === 1 ? '' : 's'})
        </button>
        <button type="button" onClick={onCreate} disabled={busy || !(summary.wouldCreate > 0)}
          className="flex items-center gap-1 px-2 py-1 text-xs border border-carbon-gray-30 text-carbon-gray-80 hover:bg-carbon-gray-10 disabled:opacity-40">
          <FilePlus size={13} /> Create {summary.wouldCreate || 0} draft(s) from the file as uploaded
        </button>
      </div>
      {findings.length > 0 && (
        <p className="text-xs text-carbon-gray-60">The corrected copy changes only the ticked cells in your own file. Upload it to create drafts from the corrected rows, and send it back to the lab.</p>
      )}
    </div>
  );
}

// What the AI did, in one block: what was sent, what came back, what it cost, and what it could not fix.
function AiSummary({ ai }) {
  const n = (ai.proposals || []).length;
  const red = (ai.proposals || []).filter((p) => p.red).length;
  const unresolved = ai.unresolved || [];
  const cost = typeof ai.costEstimateUsd === 'number' ? `about $${ai.costEstimateUsd.toFixed(4)}` : 'cost not priced';
  return (
    <div className="text-xs text-carbon-gray-80 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-purple-60 px-3 py-2 space-y-1" data-testid="ai-summary">
      {ai.sent && (
        <div>
          The AI looked at <strong>{ai.sent.cells}</strong> flagged cell(s) in {ai.sent.rows} row(s) and proposed <strong>{n}</strong> fix(es){red ? <>, <strong>{red}</strong> marked to check carefully</> : null}.
          {' '}None is applied unless you tick it.
          {ai.calls?.length ? <> Model: {ai.models?.model}{ai.escalated ? `, ${ai.escalated} cell(s) retried with ${ai.models?.escalationModel}` : ''} · {ai.inputTokens} in / {ai.outputTokens} out tokens · {cost}.</> : null}
        </div>
      )}
      {ai.aiError && <div className="text-carbon-red-60" data-testid="ai-error">AI fixer: {ai.aiError}. The findings above are unchanged; fix those cells by hand.</div>}
      {unresolved.length > 0 && (
        <div data-testid="ai-unresolved">
          Not fixed by the AI: {unresolved.map((u) => `row ${u.row} ${u.header} (${u.why})`).join('; ')}
        </div>
      )}
    </div>
  );
}
